import moment from 'moment';
import React, { useEffect, useState, useContext } from 'react';
import { useQuery } from 'react-query';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { getProfileDataByDay } from '../../NecttosComp/APICaller/ApiServices';
import { NecttosContext } from '../../context/NecttosContext';
import { useProfileData } from '../../NecttosComp/APICaller/queryHooks';

interface Category {
	title: string;
	keys: ProfileKey[];
}

interface ProfileKey {
	_id: string;
	name: string;
	shortName?: string;
	declarationText?: string;
}

interface AdmitCardProps {
	values: any;
	componentRef2: any;
	classes: any;
	type: string;
	collegeData: any;
	getQuotaTypes: (admissionTo: any) => string;
	userId: string;
}

const AdmitCard: React.FC<AdmitCardProps> = ({ values, componentRef2, classes, type, collegeData, getQuotaTypes, userId }) => {
	const { collegeId } = useContext(NecttosContext);
	const [enable, setEnable] = useState<boolean>(false);
	const [appNo, setAppNo] = useState<string>('');
	const [className, setClassName] = useState('');
	const { data: userDetail, refetch: refetchUserDetail } = useProfileData(userId);
	console.log('userDetail', userDetail);

	const { data } = useQuery(['getProfileDataTc', userId, collegeId], () => getProfileDataByDay({ userId, collegeId, days: 15 }), {
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	useEffect(() => {
		setEnable(collegeData?.collegeId !== 'COL-D6DE25F23FF5-40A6-9E76-4A2AF63D1B5F');
	}, [collegeData?.collegeId]);

	const profile = data?.profile || {};
	const keysByCategory: Category[] = Array.isArray(data?.keysByCategory) ? data?.keysByCategory : [];

	const getClassName = (dscA: any, classesData: any): string => {
		if (!dscA || !Array.isArray(classesData)) return '-';
		const matchingClass = classesData.find((cls: any) => cls.id === dscA || cls.code === dscA);
		return matchingClass?.name || dscA?.toString() || '-';
	};

	const formatFieldValue = (key: ProfileKey, value: any): string => {
		if (!value) return '-';

		if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
			const val = Object.values(value)[0];
			if (!val) return '-';
			value = val;
		}

		const label = key?.name?.toLowerCase() || '';

		if (label.includes('date') || value instanceof Date || moment(value, moment.ISO_8601, true).isValid()) {
			const parsedDate = moment(value);
			if (parsedDate.isValid()) return parsedDate.format('DD-MM-YYYY');
		}

		if (label.includes('phone') || label.includes('mobile')) {
			return value?.toString()?.slice(-10) || value?.toString();
		}

		if (label.includes('name') || label.includes('address')) {
			return value?.toString()?.toUpperCase();
		}

		return value?.toString() || 'N/A';
	};

	const declarationTexts: Record<string, string> = {
		'Anti-Narcotics Declaration': 'abhii I pledge to refrain from the use of narcotics or any other banned substances.',
		'Anti-Dowry Declaration': 'I hereby declare that I will not demand, accept, or participate in dowry practices in any form.',
		'Data Use Policy Declaration': 'I have read and understood the data use policy of the institution.',
		'Anti-Ragging Declaration': 'I hereby declare that I will not participate in or tolerate ragging in any form.',
		'Truthfulness Declaration': 'I affirm that all information provided is true and accurate to the best of my knowledge.',
		'Student Admission Declaration': 'I hereby declare that I accept the terms and conditions of admission to this institution.',
	};

	useEffect(() => {
		const fetchClassId = async () => {
			try {
				const instance = await getAxiosTokenInstance();
				const classIdResp = await instance.get('/college/admissionProcess2/getClassIdByUser', {
					params: { userId, collegeId },
				});
				const classId = classIdResp?.data?.classId;
				setClassName(classIdResp?.data?.className || '');
			} catch (error) {
				console.error('Error fetching class data:', error);
			}
		};

		if (userId && collegeId) {
			fetchClassId();
		}
	}, [userId, collegeId]);

	const getAllFields = () => {
		const fields: Array<{ label: string; value: any; category: string; declarationText?: string }> = [];

		fields.push({
			label: 'Name of Applicant',
			value: profile.name,
			category: 'Application',
		});

		fields.push({
			label: 'Admission Number',
			value: profile.admisionNo || 'N/A',
			category: 'Application',
		});

		fields.push({
			label: 'Course Name',
			value: userDetail?.className|| 'N/A',
			category: 'Application',
		});

		fields.push({
			label: 'Index As Per Allotment',
			value: profile.indexAsPerAllotment || 'N/A',
			category: 'Application',
		});

		fields.push({
			label: 'Index As Per Calculation',
			value: profile.indecAsPerCalculation || 'N/A',
			category: 'Application',
		});

		fields.push({
			label: 'CAP ID',
			value: profile.capId || 'N/A',
			category: 'Application',
		});

		fields.push({
			label: 'Mobile',
			value: profile.phoneNumber || 'N/A',
			category: 'Application',
		});

		if (values?.applicationAmount?.totalAmount) {
			fields.push({
				label: 'Fees Received',
				value: values.applicationAmount.totalAmount.toString()|| 'N/A',
				category: 'Application',
			});
		}

		if (values?.applicationAmount?.date) {
			fields.push({
				label: 'Payment Date',
				value: moment(values.applicationAmount.date).format('DD-MM-YYYY')|| 'N/A',
				category: 'Application',
			});
		}

		if (classes && profile?.dsc_a) {
			fields.push({
				label: 'Programme',
				value: getClassName(profile.dsc_a, classes),
				category: 'Application',
			});
		}

		keysByCategory.forEach((category: Category) => {
			category?.keys?.forEach((key: ProfileKey) => {
				const fieldValue = profile?.[key._id];
				const label = key?.shortName || key?.name;

				if (!label || fieldValue === undefined || fieldValue === null || (typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0)) {
					return;
				}

				if (label === 'CAP ID' || label === 'Index As Per Allotment' || label === 'Index As Per Calculation' || label === 'Mobile') {
					return;
				}

				// Handle declarations from "other" category
				if (category.title?.toLowerCase() === 'other') {
			
					const isDeclarationAccepted = fieldValue === true || fieldValue === 'true' || fieldValue === 'yes' || fieldValue === 'Yes' || fieldValue === 'YES' || (typeof fieldValue === 'object' && fieldValue !== null && (Object.values(fieldValue).includes(true) || Object.values(fieldValue).includes('true') || Object.values(fieldValue).includes('yes')));

					if (isDeclarationAccepted) {
						fields.push({
							label: label,
							value: true,
							category: 'Declarations',
							declarationText: key.declarationText || '',
						});
					}
				} else if (category.title?.toLowerCase() === 'declarations') {
					// Handle legacy declarations category
					const isDeclaration = typeof fieldValue === 'object' && fieldValue !== null && Object.values(fieldValue)[0] === true;
					if (isDeclaration) {
						fields.push({
							label: key._id,
							value: true,
							category: 'Declarations',
							declarationText: key.declarationText || '',
						});
					}
				} else {
					// Regular field (not declarations)
					const displayValue = formatFieldValue(key, fieldValue);
					if (displayValue !== '-') {
						fields.push({
							label,
							value: displayValue,
							category: category.title,
						});
					}
				}
			});
		});

		return fields;
	};

	const allFields = getAllFields();

	return (
		<div ref={componentRef2} id='printable' className='printable page-break bg-white text-xs leading-tight'>
			<style>{`
        @media print {
          .printable { font-size: 10px !important; line-height: 1.5 !important; padding: 10px !important; }
          .header-section, .field-grid, .field-item, .declaration-section, .office-section, .no-page-break, table {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .field-item { padding: 4px 4px !important; }
          @page { margin: 0.5in !important; size: A4 !important; }
          body { margin: 0 !important; padding: 0 !important; }
		  .page-break {
  page-break-before: always; /* or: page-break-after */
}
        }
      `}</style>

			<div className='header-section flex justify-between items-start mb-2 no-page-break'>
				<div className='flex items-center gap-2'>
					<img src={collegeData?.dashBoardLink} alt='Logo' className='h-12 w-12 rounded-full object-cover' />
					<div>
						<h1 className='text-sm font-bold leading-tight'>{collegeData?.collegeName}</h1>
						<h2 className='text-xs font-medium'>Admission Data Card</h2>
					</div>
				</div>
				<img src={profile?.photoURL || values?.photoURL} alt='Photo' className='h-16 w-12 object-cover rounded border' />
			</div>

			<div className='field-grid grid grid-cols-4 gap-1 text-xs no-page-break'>
				{(Array.isArray(allFields) ? allFields : [])
					.filter((field) => field.category?.toLowerCase() !== 'declarations')
					.map((field, index) => {
						const label = field?.label || '';
						const value = field?.value || '';
						const totalLength = label.length + value.length;

						let colSpan = 'col-span-2';
						if (totalLength > 100) colSpan = 'col-span-4';
						else if (totalLength > 80) colSpan = 'col-span-3';
						else if (totalLength > 50) colSpan = 'col-span-4';
						else if (totalLength > 20) colSpan = 'col-span-2';

						return (
							<div key={index} className={`field-item border border-gray-200 p-1 ${colSpan} no-page-break`}>
								<div className='flex flex-wrap items-start'>
									<span className='font-medium text-gray-700 text-xs mr-1 min-w-[80px] whitespace-nowrap'>{label}:</span>
									<span className='text-gray-900 text-xs break-words flex-1 font-semibold'>{value}</span>
								</div>
							</div>
						);
					})}
			</div>

			<div className='declaration-section mt-3 no-page-break'>
				<h3 className='text-xs font-bold mb-1 text-center'>DECLARATION</h3>
				<p className='text-xs leading-tight text-justify mb-2'> I, Mr./Ms. .........................................................., having been admitted as a student of {collegeData?.collegeName} hereby affirm that I will abide by all rules and regulations of the college. I also pledge to refrain from activities such as ragging, damaging property, disrupting classes, intentionally missing classes. I understand and accept that any violation may result in disciplinary action taken by the principal.</p>

				<div className='flex justify-between text-s mt-15'>
					<div className='space-y-5'>
						<div className='mt-5'>Parent Signature: _______________</div>
						<div className='mt-10'>Date: _______________</div>
					</div>
					<div className='space-y-5'>
						<div className='mt-5'>Student Signature: _______________</div>
						<div className='mt-10'>Student Name: _______________</div>
					</div>
				</div>
			</div>
			<div className='page-break'></div>

			{(() => {
				const declarations = getAllFields().filter((f) => f.category?.toLowerCase() === 'declarations' && f.value === true);

				return declarations.length > 0 ? (
					<div style={{ marginTop: '40px', padding: '20px', fontFamily: 'serif', fontSize: '15px', color: '#000' }}>
						<h2 style={{ textAlign: 'center', marginBottom: '30px', textDecoration: 'underline' }}>Student Declarations</h2>

						<div style={{ lineHeight: '1.75', marginBottom: '40px' }}>
							{declarations.map((decl, i) => {
								let text = decl.declarationText || declarationTexts[decl.label];
								if (!text) {
									const labelName = decl.label.replace(/([A-Z])/g, ' $1').trim();
									text = `I hereby confirm and accept the ${labelName.toLowerCase()}.`;
								}
								return text ? (
									<p key={i} style={{ marginBottom: '15px', textAlign: 'justify' }}>
										{text}
									</p>
								) : null;
							})}
						</div>

						{/* Signature Section */}
						<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '60px' }}>
							<div style={{ textAlign: 'center' }}>
								<p style={{ marginBottom: '60px' }}>Student's Signature</p>
								<div style={{ borderTop: '1px solid #000', width: '220px', margin: '0 auto' }}></div>
							</div>
							<div style={{ textAlign: 'center' }}>
								<p style={{ marginBottom: '60px' }}>Nodal Officer's Signature</p>
								<div style={{ borderTop: '1px solid #000', width: '220px', margin: '0 auto' }}></div>
							</div>
						</div>
					</div>
				) : null;
			})()}

			<div className='page-break'></div>

			<div className='office-section border-t-2 border-black mt-2 pt-1 no-page-break'>
				<div className='text-center font-bold text-xs mb-10'>FOR OFFICE USE ONLY</div>

				{['COL-D6DE25F23FF5-40A6-9E76-4A2AF63D1B5F', 'COL-08B6B8276F75-46EE-A69C-77177AAA488B'].includes(collegeData?.collegeId) ? (
					<div className='flex justify-between text-xs'>
						<div className='space-y-1'>
							<div>Admission granted: _______________</div>
							<div>Date: _______________</div>
							<div>Fees Type: _______________</div>
						</div>
						<div className='text-right'>
							<div>Signature: _______________</div>
							<div className='font-bold'>Vice Principal/Principal</div>
						</div>
					</div>
				) : (
					<div>
						<div className='font-bold text-xs mb-3'>Fee Remittance</div>
						<table className='w-full text-xs border-collapse no-page-break'>
							<thead>
								<tr>
									{['PTA', 'Special', 'CD', 'Tuition', 'Uniform', 'MISC', 'Total'].map((header) => (
										<th key={header} className='border border-gray-400 p-1 bg-gray-50'>
											{header}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								<tr>
									{Array(7)
										.fill(0)
										.map((_, i) => (
											<td key={i} className='border border-gray-400 p-5 h-3'></td>
										))}
								</tr>
							</tbody>
						</table>

						{/* Document Verification Checkboxes */}
						<div className='mt-4 mb-4'>
							<div className='font-bold text-xs mb-2'>Document Verification</div>
							<div className='grid grid-cols-3 gap-x-4 gap-y-1 text-xs'>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Degree Certificate</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Plus Two Certificates</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Mark List</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>CC</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>TC</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Nativity</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Income/Non Creamy Layer</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>TLM</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>UTL</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>NSS/NCC</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Community</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>EWS</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>PWD</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Mandatory Fee</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Admit Card</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Application</span>
								</div>
								<div className='flex items-center space-x-1'>
									<input type='checkbox' className='w-3 h-3' />
									<span>Nanmamudra/SPC</span>
								</div>
							</div>
						</div>

						<div className='flex justify-between text-xs mt-1'>
							<div className='mt-4'>Doc. Verified: ___________</div>
							<div className='mt-4'>Node Officer: ___________</div>
							<div className='mt-4'>Superintendent: _________</div>
							<div className='font-bold mt-4'>Principal: ___________</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AdmitCard;
